import React from 'react';
import '../assets/Reports.css';
import Card from '../reports/Card';
import SingleStatCard from '../reports/SingleStatCard';
import CompactPieChart from '../reports/CompactPieChart';
import HistogramChart from '../reports/HistogramChart';
import BootKpiTable from '../reports/BootKpiTable';
import TimeRangeSelector from '../reports/TimeRangeSelector';
import { LoadingSkeleton } from '../reports/LoadingSkeleton';
import deviceDataService from '../../services/deviceDataService';
import ThroughputStatsSection from '../reports/ThroughputStatsSection';

// ─── Device Group Selector ────────────────────────────────────────────────────

function DeviceGroupSelector({ groups, selectedGroup, onChange, disabled }) {
    const [isOpen, setIsOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const containerRef = React.useRef(null);

    // Close on outside click
    React.useEffect(() => {
        const handleOutsideClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    const filtered = groups.filter(g =>
        g.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelect = (group) => {
        onChange(group);
        setIsOpen(false);
        setSearch('');
    };

    return (
        <div className="dg-selector" ref={containerRef}>
            <button
                className={`dg-trigger${isOpen ? ' open' : ''}${disabled ? ' disabled' : ''}`}
                onClick={() => !disabled && setIsOpen(o => !o)}
                disabled={disabled}
                type="button"
            >
                <span className="dg-trigger-icon">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.7"/>
                        <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor"/>
                        <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.4"/>
                        <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.7"/>
                    </svg>
                </span>
                <span className="dg-trigger-label">Device Group</span>
                <span className="dg-trigger-value">
                    {selectedGroup || <span className="dg-placeholder">Select a group…</span>}
                </span>
                <span className={`dg-chevron${isOpen ? ' rotated' : ''}`}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </span>
            </button>

            {isOpen && (
                <div className="dg-panel">
                    <div className="dg-search-wrap">
                        <svg className="dg-search-icon" width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.6"/>
                            <path d="M10 10L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                        </svg>
                        <input
                            className="dg-search"
                            type="text"
                            placeholder="Search device groups…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            autoFocus
                        />
                        {search && (
                            <button className="dg-search-clear" onClick={() => setSearch('')} type="button">
                                ×
                            </button>
                        )}
                    </div>

                    <div className="dg-list">
                        {filtered.length === 0 ? (
                            <div className="dg-empty">No matching groups</div>
                        ) : (
                            filtered.map(group => (
                                <button
                                    key={group}
                                    className={`dg-item${group === selectedGroup ? ' active' : ''}`}
                                    onClick={() => handleSelect(group)}
                                    type="button"
                                >
                                    <span className={`dg-item-dot${group === selectedGroup ? ' active' : ''}`} />
                                    <span className="dg-item-name">{group}</span>
                                    {group === selectedGroup && (
                                        <svg className="dg-check" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                            <path d="M2 7L5.5 10.5L12 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            ))
                        )}
                    </div>

                    <div className="dg-footer">
                        <span className="dg-count">{groups.length} Device Group{groups.length !== 1 ? 's' : ''}</span>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Main View ────────────────────────────────────────────────────────────────

function DeviceGroupView() {
    const [selectedTimeRange, setSelectedTimeRange] = React.useState('7d');
    const [selectedGroup, setSelectedGroup] = React.useState(null);
    const [availableGroups, setAvailableGroups] = React.useState([]);
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [throughput, setThroughput] = React.useState({ data: null, loading: false, error: null });
    const [groupsLoading, setGroupsLoading] = React.useState(true);

    // Scroll-based time selector visibility
    const [isTimeSelectorVisible, setIsTimeSelectorVisible] = React.useState(true);
    const lastScrollY = React.useRef(0);

    // Load available device groups on mount
    React.useEffect(() => {
        setGroupsLoading(true);
        deviceDataService.getAggregateTags().then(result => {
            const groups = result.tags || [];
            setAvailableGroups(groups);
            if (groups.length > 0) {
                setSelectedGroup(groups[0]);
            }
        }).catch(err => {
            console.warn('Could not load device groups:', err);
        }).finally(() => {
            setGroupsLoading(false);
        });
    }, []);

    // Load dashboard data for the selected group
    const loadGroupData = React.useCallback(async (timeRange, group) => {
        if (!group) return;

        try {
            setLoading(true);
            setError(null);
            console.log('🔄 Loading Device Group data:', group, 'range:', timeRange);

            const dashboardData = await deviceDataService.getDeviceTagDashboard(group, timeRange);

            if (dashboardData?.cacheError) {
                console.log('📭 Cache error:', dashboardData.message);
                setData(null);
                setError({
                    type: 'cache_error',
                    message: dashboardData.message,
                    details: dashboardData.details,
                    timeRange: dashboardData.timeRange
                });
            } else {
                setData(dashboardData);
                console.log('✅ Device Group data loaded');
            }
        } catch (err) {
            console.error('❌ Error loading Device Group data:', err);
            setError({
                type: 'network_error',
                message: err.message,
                details: 'An unexpected error occurred while loading data.'
            });
        } finally {
            setLoading(false);
        }
    }, []);

    const loadGroupThroughput = React.useCallback(async (timeRange, group) => {
        if (!group) return;

        try {
            setThroughput(prev => ({ ...prev, loading: true, error: null }));
            const result = await deviceDataService.getThroughputStats(timeRange, group);
            setThroughput({ data: result, loading: false, error: null });
        } catch (err) {
            setThroughput({ data: null, loading: false, error: { message: err.message } });
        }
    }, []);

    // Reload when group or time range changes
    React.useEffect(() => {
        if (selectedGroup) {
            loadGroupData(selectedTimeRange, selectedGroup);
            loadGroupThroughput(selectedTimeRange, selectedGroup);
        }
    }, [selectedTimeRange, selectedGroup, loadGroupData, loadGroupThroughput]);

    const handleTimeRangeChange = (range) => setSelectedTimeRange(range);
    const handleGroupChange = (group) => setSelectedGroup(group);
    const handleRefresh = () => {
        loadGroupData(selectedTimeRange, selectedGroup);
        loadGroupThroughput(selectedTimeRange, selectedGroup);
    };

    // Scroll detection
    React.useEffect(() => {
        let scrollableElement = null;
        let cleanupListeners = [];

        const handleScroll = (event) => {
            const currentScrollY = event.target.scrollTop || window.scrollY;
            if (Math.abs(currentScrollY - lastScrollY.current) < 5) return;

            if (currentScrollY > lastScrollY.current && currentScrollY > 20) {
                setIsTimeSelectorVisible(false);
            } else if (currentScrollY < lastScrollY.current) {
                setIsTimeSelectorVisible(true);
            }
            lastScrollY.current = currentScrollY;
        };

        setTimeout(() => {
            const allElements = document.querySelectorAll('*');
            for (let el of allElements) {
                if (el.scrollHeight > el.clientHeight) {
                    const { overflowY } = window.getComputedStyle(el);
                    if (overflowY === 'scroll' || overflowY === 'auto') {
                        scrollableElement = el;
                        break;
                    }
                }
            }
            if (scrollableElement) {
                scrollableElement.addEventListener('scroll', handleScroll, { passive: true });
                cleanupListeners.push(() => scrollableElement.removeEventListener('scroll', handleScroll));
            } else {
                window.addEventListener('scroll', handleScroll, { passive: true });
                document.addEventListener('scroll', handleScroll, { passive: true });
                cleanupListeners.push(() => window.removeEventListener('scroll', handleScroll));
                cleanupListeners.push(() => document.removeEventListener('scroll', handleScroll));
            }
        }, 100);

        return () => cleanupListeners.forEach(fn => fn());
    }, []);

    // ── Render helpers ──────────────────────────────────────────────────────

    const renderLoadingSkeleton = () => (
        <div className="reports-container">
            <Card title="Overview" collapsible={true}>
                <div className="overview-stats-grid">
                    <LoadingSkeleton height="120px" />
                </div>
            </Card>
            <Card title="Network Health" collapsible={true}>
                <div className="network-health-pie-charts">
                    <LoadingSkeleton height="200px" />
                    <LoadingSkeleton height="200px" />
                    <LoadingSkeleton height="200px" />
                    <LoadingSkeleton height="200px" />
                    <LoadingSkeleton height="200px" />
                </div>
                <div className="network-health-signal-grid">
                    <LoadingSkeleton height="300px" />
                    <LoadingSkeleton height="300px" />
                    <LoadingSkeleton height="300px" />
                </div>
            </Card>
            <Card title="Boot KPI" collapsible={true}>
                <div className="boot-kpi-charts">
                    <LoadingSkeleton height="300px" />
                    <LoadingSkeleton height="300px" />
                </div>
            </Card>
        </div>
    );

    const renderNoGroupSelected = () => (
        <div className="reports-container">
            <div className="dg-empty-state">
                <div className="dg-empty-state-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <rect x="4" y="4" width="18" height="18" rx="3" fill="#1a73e8" opacity="0.8"/>
                        <rect x="26" y="4" width="18" height="18" rx="3" fill="#1a73e8" opacity="0.4"/>
                        <rect x="4" y="26" width="18" height="18" rx="3" fill="#1a73e8" opacity="0.4"/>
                        <rect x="26" y="26" width="18" height="18" rx="3" fill="#1a73e8" opacity="0.2"/>
                    </svg>
                </div>
                <h3>No Device Group Selected</h3>
                <p>
                    {groupsLoading
                        ? 'Loading available device groups…'
                        : availableGroups.length === 0
                            ? 'No device groups with aggregated data are available.'
                            : 'Select a device group above to view its dashboard.'}
                </p>
            </div>
        </div>
    );

    const renderError = () => {
        if (error?.type === 'cache_error') {
            return (
                <div className="reports-container">
                    <Card title="Device Group Dashboard" collapsible={false}>
                        <div className="cache-error-message" style={{
                            textAlign: 'center',
                            padding: '40px 20px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef'
                        }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
                            <h3 style={{ color: '#495057', marginBottom: '12px' }}>Data Not Available</h3>
                            <p style={{ color: '#6c757d', marginBottom: '8px', fontSize: '16px' }}>
                                {error.message}
                            </p>
                            {error.details && (
                                <p style={{ color: '#868e96', fontSize: '14px', marginBottom: '20px' }}>
                                    {error.details}
                                </p>
                            )}
                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                {['1d', '7d', '14d', '30d'].map(range => (
                                    <button
                                        key={range}
                                        onClick={() => handleTimeRangeChange(range)}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: selectedTimeRange === range ? '#007bff' : '#f8f9fa',
                                            color: selectedTimeRange === range ? 'white' : '#495057',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {range === '1d' ? '1 Day' : range === '7d' ? '7 Days' : range === '14d' ? '14 Days' : '30 Days'}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </Card>
                </div>
            );
        }

        return (
            <div className="reports-container">
                <div className="error-message">
                    <span className="error-icon">⚠️</span>
                    <div>
                        <strong>Error loading Device Group data:</strong>
                        <br />
                        {error?.message || error}
                        <br />
                        {error?.details && (
                            <>
                                <small style={{ color: '#666' }}>{error.details}</small>
                                <br />
                            </>
                        )}
                        <button onClick={handleRefresh} style={{ marginTop: '10px', padding: '5px 10px' }}>
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ── Main render ─────────────────────────────────────────────────────────

    return (
        <div className="aggregate-device-report">
            {/* Sticky header */}
            <div className={`aggregate-time-selector${!isTimeSelectorVisible ? ' hidden' : ''}`}>
                <TimeRangeSelector
                    selectedRange={selectedTimeRange}
                    onRangeChange={handleTimeRangeChange}
                    disabled={loading || !selectedGroup}
                    onRefresh={selectedGroup ? handleRefresh : null}
                    isRefreshing={loading}
                    preset="aggregate"
                />
                <div style={{ marginLeft: '16px' }}>
                    <DeviceGroupSelector
                        groups={availableGroups}
                        selectedGroup={selectedGroup}
                        onChange={handleGroupChange}
                        disabled={groupsLoading}
                    />
                </div>
            </div>

            {/* Content */}
            {!selectedGroup ? (
                renderNoGroupSelected()
            ) : loading && !data ? (
                renderLoadingSkeleton()
            ) : error ? (
                renderError()
            ) : (
                <div className="reports-container">
                    {/* Overview */}
                    <Card title="Overview" collapsible={true}>
                        <div className="overview-stats-grid">
                            <SingleStatCard
                                title="Total Devices Seen"
                                value={data?.overview?.totalDevices || 0}
                                subtitle={`Devices in group: ${selectedGroup}`}
                                color="#1a73e8"
                            />
                        </div>
                    </Card>

                    <ThroughputStatsSection
                        data={throughput.data}
                        loading={throughput.loading}
                        error={throughput.error}
                        timeRange={selectedTimeRange}
                    />

                    {/* Network Health */}
                    <Card title="Network Health" collapsible={true}>
                        <div className="network-health-pie-charts">
                            <CompactPieChart
                                title="NAS Registration Status"
                                data={data?.networkHealth?.registrationStatus?.data || []}
                                labels={data?.networkHealth?.registrationStatus?.labels || []}
                                colors={data?.networkHealth?.registrationStatus?.colors || []}
                            />
                            <CompactPieChart
                                title="NAS Registration Failure Reasons"
                                data={data?.networkHealth?.registrationFailures?.data || []}
                                labels={data?.networkHealth?.registrationFailures?.labels || []}
                                colors={data?.networkHealth?.registrationFailures?.colors || []}
                            />
                            <CompactPieChart
                                title="PDN Status"
                                data={data?.networkHealth?.pdnStatus?.data || []}
                                labels={data?.networkHealth?.pdnStatus?.labels || []}
                                colors={data?.networkHealth?.pdnStatus?.colors || []}
                            />
                            <CompactPieChart
                                title="PDN Failure Reasons"
                                data={data?.networkHealth?.pdnFailureReasons?.data || []}
                                labels={data?.networkHealth?.pdnFailureReasons?.labels || []}
                                colors={data?.networkHealth?.pdnFailureReasons?.colors || []}
                            />
                            <CompactPieChart
                                title="PDN APN Type Distribution"
                                data={data?.networkHealth?.pdnApnTypeDistribution?.data || []}
                                labels={data?.networkHealth?.pdnApnTypeDistribution?.labels || []}
                                colors={data?.networkHealth?.pdnApnTypeDistribution?.colors || []}
                            />
                        </div>

                        <div className="network-health-signal-histograms">
                            <HistogramChart
                                title="Signal Strength (RSRP)"
                                bins={data?.networkHealth?.rsrpHistogram?.bins || []}
                                xAxisLabel="RSRP (dBm)"
                                yAxisLabel="Number of Instances"
                                color="#3498db"
                            />
                            <HistogramChart
                                title="Signal Strength (RSRQ)"
                                bins={data?.networkHealth?.rsrqHistogram?.bins || []}
                                xAxisLabel="RSRQ (dB)"
                                yAxisLabel="Number of Instances"
                                color="#e74c3c"
                            />
                            <HistogramChart
                                title="Signal Strength (SINR)"
                                bins={data?.networkHealth?.sinrHistogram?.bins || []}
                                xAxisLabel="SINR (dB)"
                                yAxisLabel="Number of Instances"
                                color="#2ecc71"
                            />
                        </div>
                    </Card>

                    {/* Boot KPI */}
                    <Card title="Boot KPI" collapsible={true}>
                        <div className="boot-kpi-pie-row">
                            <CompactPieChart
                                title="Boot KPI Type Distribution"
                                data={data?.bootKpi?.kpiDistribution?.data || []}
                                labels={data?.bootKpi?.kpiDistribution?.labels || []}
                                colors={data?.bootKpi?.kpiDistribution?.colors || []}
                            />
                        </div>
                        <BootKpiTable timingHistograms={data?.bootKpi?.timingHistograms || {}} />
                    </Card>
                </div>
            )}
        </div>
    );
}

export default DeviceGroupView;
